// ─────────────────────────────────────────────────────────────────────────────
// File: frontend/src/components/AdminRoute.tsx
// ─────────────────────────────────────────────────────────────────────────────
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

// ══════════════════════════════════════════════════════════════════════════════
// TYPES
// ══════════════════════════════════════════════════════════════════════════════

type Props = {
  children: ReactNode;
};

interface User {
  uid: string;
  email: string | null;
  isAdmin?: boolean;
  role?: string;
  claims?: {
    admin?: boolean;
  };
  customClaims?: {
    admin?: boolean;
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// ENVIRONMENT & SECURITY
// ══════════════════════════════════════════════════════════════════════════════

const ENV = import.meta.env.MODE || "production";
const IS_DEV = ENV === "development";
const ALLOW_LAYOUT_EDITOR = import.meta.env.VITE_ALLOW_LAYOUT_EDITOR === "1";

// 🔒 SECURITY: Only allow dev bypasses in actual development mode
const ALLOW_DEV_BYPASS = IS_DEV && ALLOW_LAYOUT_EDITOR;

// ══════════════════════════════════════════════════════════════════════════════
// ADMIN VERIFICATION
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Check if user has admin privileges based on Firebase claims.
 * 
 * 🔒 SECURITY: Server-side claims are verified by Firebase Auth.
 * Client-side checks are for UX only - backend MUST verify permissions!
 */
function isAdminUser(user: User | null): boolean {
  if (!user) return false;

  return Boolean(
    user.isAdmin ||
    user.claims?.admin ||
    user.customClaims?.admin ||
    user.role === "admin"
  );
}

/**
 * Centralized admin check.
 * 
 * 🔒 PRODUCTION: Only checks Firebase claims (server-verified).
 * 🔧 DEVELOPMENT: Allows ?dev=1 bypass for local testing only.
 * 
 * ⚠️ CRITICAL: Backend MUST verify admin status independently!
 * Never trust client-side checks for authorization!
 */
function useIsAdmin(user: User | null) {
  const location = useLocation();

  // Check server-verified admin claim (primary method)
  const hasAdminClaim = isAdminUser(user);

  // 🔧 DEVELOPMENT ONLY: Allow URL bypass
  if (ALLOW_DEV_BYPASS) {
    const params = new URLSearchParams(location.search);
    const devParam = params.get("dev") === "1";

    if (devParam) {
      console.warn("⚠️ DEV MODE: Admin access granted via ?dev=1");
      return true;
    }
  }

  return hasAdminClaim;
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ══════════════════════════════════════════════════════════════════════════════

/**
 * AdminRoute - Protects routes that require admin privileges.
 * 
 * Security:
 * - Redirects unauthenticated users to login
 * - Shows 403 for authenticated non-admin users
 * - Checks Firebase custom claims (server-verified)
 * - Dev bypass only in development mode
 * 
 * Usage:
 * ```tsx
 * <Route path="/admin/*" element={
 *   <AdminRoute>
 *     <AdminDashboard />
 *   </AdminRoute>
 * } />
 * ```
 */
export default function AdminRoute({ children }: Props) {
  const { user, loading } = useAuth() as { user: User | null; loading: boolean };
  const location = useLocation();
  const isAdmin = useIsAdmin(user);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    const next = `${location.pathname}${location.search || ""}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }

  // Show 403 if authenticated but not admin
  if (!isAdmin) {
    return (
      <div className="p-6 max-w-3xl mx-auto text-slate-900 dark:text-slate-100">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <h1 className="text-2xl font-semibold text-red-600 dark:text-red-400 mb-3">
            🔒 Access Denied
          </h1>

          <p className="text-slate-700 dark:text-slate-300 mb-4">
            You're signed in as <strong>{user.email}</strong>, but your account doesn't have admin privileges.
          </p>

          <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 p-4">
            <h2 className="font-medium text-slate-900 dark:text-slate-100 mb-2">
              Need Admin Access?
            </h2>
            <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-2 list-disc list-inside">
              <li>Contact your system administrator to request admin privileges</li>
              <li>Admins can grant access through Firebase Authentication console</li>
              {ALLOW_DEV_BYPASS && (
                <li className="text-blue-600 dark:text-blue-400">
                  <strong>Dev Mode:</strong> Add <code>?dev=1</code> to the URL for temporary access
                </li>
              )}
            </ul>
          </div>

          <div className="mt-6 flex gap-3">
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition"
            >
              ← Go Back
            </button>
            <a
              href="/"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Go Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  // User is authenticated and has admin privileges
  return <>{children}</>;
}

// ══════════════════════════════════════════════════════════════════════════════
// HELPER HOOK FOR OTHER COMPONENTS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Hook to check if current user is admin.
 * 
 * Usage in components:
 * ```tsx
 * const isAdmin = useAdminStatus();
 * if (isAdmin) {
 *   return <AdminPanel />;
 * }
 * ```
 * 
 * ⚠️ Remember: This is client-side only! Backend must verify independently.
 */
export function useAdminStatus(): boolean {
  const { user } = useAuth() as { user: User | null };
  return useIsAdmin(user);
}